/**
 * ★ Fase 55 — Mini-landing prodotto DEDICATE (usabili nelle campagne dirette).
 *  - /f/it-bali   → Villa Flora (Bali) · 7 grafiche verticali
 *  - /f/it-lombok → Green Paradise Lombok · 6 grafiche verticali
 * Le grafiche (già complete di titoli, badge e CTA) compongono la landing
 * impilate 1→N senza interruzioni. In fondo: form lead (stesso flusso del
 * funnel IT-AL → routing a Claudia) + CTA Calendly diretto col CEO.
 * ★ Fase 82 — versioni ES (/f/es-ba, /f/es-lo): le grafiche IT (testo cotto
 * nell'immagine) sono sostituite da SLIDE HTML in spagnolo con le stesse
 * foto (vedi components/funnels/IndonesiaSlides.jsx, parametrico per lingua).
 * ★ Fase 85 — versioni EN (/f/en-ba, /f/en-lo): stesse slide HTML in
 * inglese · lead EN-BA / EN-LO → Richard (tenant EN) · CTA Calendly Richard.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import i18n from '../../../i18n';
import { api } from '../../../lib/api';
import { toast } from 'sonner';
import { TestimonialsSection } from '../../../components/funnels/TestimonialsSection';
import { VillaFloraSlidesES, GreenParadiseSlidesES, VillaFloraSlidesEN, GreenParadiseSlidesEN } from '../../../components/funnels/IndonesiaSlides';
import { setPageMeta, ogImageFor } from '../../../lib/seo';
import { captureAttribution, getAttribution, trackFunnelEvent } from '../../../lib/attribution';
import { NoObligationNote } from '../../../components/funnels/NoObligationNote';
import { PrequalFields, cleanPrequal } from '../../../components/funnels/PrequalFields';
import { FaqAccordion } from '../../../components/funnels/FaqAccordion';
import {
  IntroHero, ProofStrip, StepPath, ModelTerms, CeoCallCard, NextStepsSuccess, StickyCta,
} from '../../../components/funnels/AssetLandingBlocks';
import {
  ArrowRight, User, Mail, Phone, Loader2, FileCheck2, ShieldCheck,
  CheckCircle2, CalendarClock, Images, FileText, UserRound, Percent, BadgeEuro, Scale, CalendarDays, Utensils, Users,
} from 'lucide-react';

const INK = '#081712';
const EMERALD = '#0C2B21';
const GOLD = '#D6B25E';
const IVORY = '#F4EEE1';
const SAND = '#CFC6B2';

/* Fallback: il valore live arriva da GET /public/funnel/it-al (booking_url). */
const CALENDLY_FALLBACK = 'https://calendly.com/legal-housecashflows/colloquio-strategico-con-il-ceo-di-hcf-group';

const LUX_CSS = `
  .aml-lux-field { background: rgba(244,238,225,0.07); border: 1px solid rgba(214,178,94,0.30); color: #F4EEE1; caret-color: #D6B25E; transition: border-color .2s, box-shadow .2s, background-color .2s; }
  .aml-lux-field::placeholder { color: rgba(207,198,178,0.55); }
  .aml-lux-field:hover:not(:focus) { border-color: rgba(214,178,94,0.55); }
  .aml-lux-field:focus { border-color: #D6B25E; background: rgba(244,238,225,0.12); box-shadow: 0 0 0 3px rgba(214,178,94,0.20); outline: none; }
  .aml-gold-btn { background: linear-gradient(135deg, #E3C87A 0%, #D6B25E 45%, #B8860B 100%); color: #11271E; box-shadow: 0 10px 30px rgba(214,178,94,0.22), inset 0 1px 0 rgba(255,255,255,0.35); transition: transform .2s, box-shadow .2s; }
  .aml-gold-btn:hover:not(:disabled) { transform: scale(1.015); box-shadow: 0 14px 42px rgba(214,178,94,0.34), inset 0 1px 0 rgba(255,255,255,0.4); }
  .aml-gold-btn:active:not(:disabled) { transform: scale(0.99); }
  .aml-gold-btn:disabled { opacity: 0.5; }
`;

/* ── Testi UI per lingua (★ Fase 59 — versioni ES delle mini-landing) ── */
const UI_TEXTS = {
  it: {
    headerCta: 'Consulenza col CEO',
    toastOk: 'Richiesta inviata!',
    toastOkDesc: 'Un Senior Associate ti contatterà a breve.',
    toastOkDescBrochure: 'Ti abbiamo inviato la brochure via email. Un Senior Associate ti contatterà a breve.',
    toastErr: 'Invio non riuscito, riprova.',
    successTitle: 'Richiesta ricevuta',
    successTitleBrochure: 'Brochure in arrivo!',
    successDesc: (name) => `Un Senior Associate ti contatterà a breve per ${name}. Se preferisci, prenota subito una consulenza strategica col CEO.`,
    successDescBrochure: (name) => `Ti abbiamo appena inviato via email la brochure ufficiale di ${name}. Un Senior Associate ti contatterà a breve. Se preferisci, prenota subito una consulenza strategica col CEO.`,
    bookRichard: 'Prenota una consulenza strategica col CEO',
    phName: 'Nome e cognome *',
    phEmail: 'Email *',
    phPhone: 'Telefono (consigliato)',
    noObligation: 'Nessun obbligo di acquisto',
    backPrompt: 'Vuoi il quadro completo del percorso HCF?',
    backLink: 'Vedi la pagina principale',
    footerPrivacy: 'Privacy', footerTerms: 'Termini', footerCookies: 'Cookie',
    sectionAlt: (name, i, n) => `${name} — sezione ${i} di ${n}`,
    legal: 'Inviando accetti la nostra', privacy: 'Privacy Policy', emailInvalid: 'Inserisci un indirizzo email valido.',
  },
  es: {
    headerCta: 'Reserva la consultoría',
    toastOk: '¡Solicitud enviada!',
    toastOkDesc: 'Un Senior Associate te contactará en breve.',
    toastErr: 'No se pudo enviar, inténtalo de nuevo.',
    successTitle: 'Solicitud recibida',
    successDesc: (name) => `Un Senior Associate te contactará en breve sobre ${name}. Si lo prefieres, reserva ya tu consultoría estratégica.`,
    bookRichard: 'Reserva la consultoría estratégica',
    phName: 'Nombre y apellidos *',
    phEmail: 'Email *',
    phPhone: 'Teléfono (recomendado)',
    noObligation: 'Sin obligación de compra',
    backPrompt: '¿Quieres el panorama completo del recorrido HCF?',
    backLink: 'Ver la página principal',
    footerPrivacy: 'Privacidad', footerTerms: 'Términos', footerCookies: 'Cookies',
    sectionAlt: (name, i, n) => `${name} — sección ${i} de ${n}`,
    legal: 'Al enviar aceptas nuestra', privacy: 'Política de privacidad', emailInvalid: 'Introduce un email válido.',
  },
  en: {
    headerCta: 'Book the consultation',
    toastOk: 'Request sent!',
    toastOkDesc: 'A Senior Associate will contact you shortly.',
    toastErr: 'Could not send, please try again.',
    successTitle: 'Request received',
    successDesc: (name) => `A Senior Associate will contact you shortly about ${name}. If you prefer, book your strategic consultation with the CEO right away.`,
    bookRichard: 'Book a strategic consultation with the CEO',
    phName: 'Full name *',
    phEmail: 'Email *',
    phPhone: 'Phone (recommended)',
    noObligation: 'No obligation to buy',
    backPrompt: 'Want the full picture of the HCF journey?',
    backLink: 'See the main page',
    footerPrivacy: 'Privacy', footerTerms: 'Terms', footerCookies: 'Cookies',
    sectionAlt: (name, i, n) => `${name} — section ${i} of ${n}`,
    legal: 'By submitting you accept our', privacy: 'Privacy Policy', emailInvalid: 'Enter a valid email address.',
  },
};

/* ── Config delle mini-landing (slug URL = acronimo CRM minuscolo) ──
   ★ Fase 57 — codici ufficiali: IT-BA (Bali) e IT-LO (Lombok); i lead
   vengono taggati col funnel stesso (funnel_code IT-BA / IT-LO nel CRM).
   ★ Fase 59 — versioni ES (ES-BA / ES-LO): lead → Sofia, UI in spagnolo.
   ★ Fase 82 — ES: `slidesComponent` = slide HTML in spagnolo (stesse foto). */
const ASSET_LANDINGS = {
  'it-ba': {
    code: 'IT-BA',
    lang: 'it',
    homeHref: '/f/it',
    funnelApi: '/public/funnel/it',
    propertySlug: 'villa-flora',
    name: 'Villa Flora',
    place: 'Bali · Indonesia',
    docTitle: 'Villa Flora Bali — HCF Property',
    docDescription: 'Villa Flora Bali: villa intima con piscina privata, interni luminosi e design boho-minimal. Richiedi disponibilità con HCF Property.',
    slides: [1, 2, 3, 4, 5, 6, 7].map((n) => `/funnels/indonesia/villa-flora/0${n}.webp`),
    cta: 'Richiedi disponibilità',
    formKicker: 'Richiedi disponibilità · Villa Flora Bali',
    leadMessage: 'Richiesta disponibilità — Villa Flora (Bali, Indonesia)',
    // ★ Fase 92 (Doc 02) — richiesta disponibilità SEPARATA dalla call high-value,
    //   proposta concreta sopra la piega, micro-proof, prequalifica progressiva,
    //   conferma + prossimo step, CTA ripetuta coerente. Nessun dato inventato:
    //   TODO (plan.md) → stato progetto e prove datate specifiche Bali non disponibili.
    v2: {
      ctaPrimary: 'Richiedi disponibilità aggiornata e dossier Villa Flora',
      ctaShort: 'Richiedi disponibilità',
      intent: 'availability',
      intro: {
        kicker: 'Bali · Indonesia · Villa Flora',
        title: 'Una villa privata con piscina a Bali. Prima la disponibilità reale, poi la decisione.',
        subtitle: 'Villa Flora è la villa HCF a Bali: piscina privata immersa nel verde, interni luminosi, living con kitchenette e materiali naturali. Lascia i tuoi contatti: verifichiamo la disponibilità aggiornata e ti inviamo il dossier, senza impegno.',
        facts: [
          { id: 'location', label: 'Localizzazione', value: 'Bali, Indonesia' },
          { id: 'asset', label: 'Asset', value: 'Villa privata con piscina', note: 'Living + kitchenette · camera · bagno' },
          { id: 'availability', label: 'Disponibilità', value: 'Su richiesta', note: 'Verificata prima di ogni risposta' },
          // TODO (Doc 02): stato progetto non confermato → riga nascosta finché non validato
          { id: 'status', label: 'Stato progetto', value: '' },
        ],
        cta: 'Richiedi disponibilità aggiornata e dossier Villa Flora',
        ctaHint: 'Nessuna call obbligatoria: prima il dossier, poi decidi tu.',
      },
      proof: {
        kicker: 'Cosa puoi verificare prima di decidere',
        title: 'Un dossier da leggere con calma, non una decisione di impulso.',
        items: [
          { icon: Images, t: 'Gallery degli ambienti', d: 'Piscina, living, camera, bagno e kitchenette: le immagini che vedi in questa pagina.' },
          { icon: FileText, t: 'Dossier Villa Flora', d: 'Caratteristiche, condizioni e disponibilità aggiornata, inviato dal team HCF dopo la richiesta.' },
          { icon: UserRound, t: 'Confronto con un Senior Associate', d: 'Per domande su gestione, costi e tempi, prima di qualsiasi scelta.' },
        ],
        note: 'Documenti, date e aggiornamenti specifici su Bali vengono condivisi nel dossier solo se verificati.',
      },
      ceoCall: {
        kicker: 'Percorso separato · alto valore',
        title: 'Vuoi ragionare sulla struttura patrimoniale complessiva?',
        text: 'La consulenza strategica col CEO di HCF Group non serve a chiedere disponibilità: è un confronto per chi vuole capire come un asset a Bali si inserisce in una visione patrimoniale più ampia.',
        cta: 'Prenota la consulenza col CEO',
      },
      success: {
        title: 'Richiesta ricevuta',
        subtitle: 'Ecco cosa succede ora.',
        steps: [
          'Verifichiamo la disponibilità aggiornata di Villa Flora.',
          'Il team HCF ti invia il dossier via email.',
          'Un Senior Associate ti contatta per rispondere alle tue domande.',
        ],
        bookingCta: 'Vuoi anticipare? Prenota la consulenza col CEO',
        bookingNote: 'Facoltativa · percorso separato dalla richiesta di disponibilità.',
      },
    },
  },
  'it-lo': {
    // ★ Fase 68 — al submit del form il lead riceve SUBITO la brochure via email
    brochureEmail: true,
    code: 'IT-LO',
    lang: 'it',
    homeHref: '/f/it',
    funnelApi: '/public/funnel/it',
    propertySlug: 'green-paradise-lombok',
    name: 'Green Paradise Lombok',
    place: 'Bumbang Bay · Lombok · Indonesia',
    docTitle: 'Green Paradise Lombok — HCF Property',
    docDescription: 'Green Paradise Lombok: ville di lusso a Bumbang Bay. Prima si verifica, poi si analizza, solo dopo si sceglie la villa. Prenota la verifica sul posto con HCF Property.',
    slides: [1, 2, 3, 4, 5, 6].map((n) => `/funnels/indonesia/green-paradise-lombok/0${n}.webp`),
    cta: 'Prenota la verifica sul posto',
    formKicker: 'Verifica sul posto · Green Paradise Lombok',
    leadMessage: 'Richiesta verifica / analisi strategica — Green Paradise Lombok (Indonesia)',
    // ★ Fase 93 (Doc 02) — percorso in 2 step (dossier/Zoom → verifica in loco), modello
    //   dichiarato con nota "soggetto a verifica", FAQ costi/rischi/disponibilità/uscita/fiscalità
    //   senza promesse. Dati: scheda prodotto CRM (32 ville — ripartizione per tipologia da confermare —, da €116.000, apertura ott. 2027).
    v2: {
      ctaPrimary: 'Scopri il modello e richiedi una valutazione iniziale',
      ctaShort: 'Valutazione iniziale',
      intent: 'dossier',
      intro: {
        kicker: 'Bumbang Bay · Lombok · Indonesia · Green Paradise',
        title: 'Un resort di 32 ville a Bumbang Bay. Prima il modello e il dossier, poi la verifica sul posto.',
        subtitle: 'Green Paradise Lombok è il resort HCF fronte oceano nel sud di Lombok: 32 ville in due tipologie (Villa Green e Villa Paradise) gestite come resort. Qui trovi come funziona il modello, cosa include e cosa non include, e il percorso in due step per verificarlo.',
        facts: [
          { id: 'location', label: 'Localizzazione', value: 'Bumbang Bay, sud di Lombok (Indonesia)' },
          { id: 'asset', label: 'Asset', value: '32 ville private in resort', note: 'Villa Green · Villa Paradise' },
          { id: 'status', label: 'Stato progetto', value: 'Apertura prevista ottobre 2027', note: 'Data dichiarata · soggetta a verifica' },
          { id: 'price', label: 'Prezzi dichiarati', value: 'Da €116.000', note: 'Caparra + anticipo + 5 rate + saldo · soggetti a verifica' },
        ],
        cta: 'Scopri il modello e richiedi una valutazione iniziale',
        ctaHint: 'Step 1: dossier + Zoom. La verifica in loco viene dopo, solo se ha senso.',
      },
      path: {
        id: 'percorso',
        kicker: 'Percorso in 2 step',
        title: 'Prima il dossier e la Zoom. Poi, solo se ha senso, la verifica in loco.',
        intro: 'Nessuna visita «al buio»: si parte da dati e condizioni, si valuta in Zoom e si va a Lombok solo quando il progetto è coerente con il tuo obiettivo.',
        steps: [
          {
            k: 'Step 1 · Dossier + Zoom',
            t: 'Dossier Green Paradise e valutazione iniziale',
            d: 'Lasci i contatti: ricevi subito via email la brochure ufficiale (magazine e planimetrie) e un Senior Associate fissa con te una valutazione iniziale in Zoom su obiettivo, modello e numeri.',
            bullets: ['Brochure ufficiale subito via email', 'Valutazione iniziale in Zoom: obiettivo, modello, condizioni', 'Risposte su costi, rischi, tempi e uscita'],
            cta: 'Richiedi dossier e valutazione iniziale',
          },
          {
            k: 'Step 2 · Verifica in loco',
            t: 'Verifica sul posto a Bumbang Bay',
            d: 'Solo dopo lo Step 1, se il progetto è coerente con il tuo profilo: visita del sito, incontro con il team e lettura del contesto reale prima di qualsiasi decisione.',
            bullets: ['Visita del sito e del contesto', 'Confronto con il team sul posto', 'Decisione solo dopo la verifica'],
            note: 'Modalità, date e costi della verifica in loco vengono comunicati dopo lo Step 1.',
          },
        ],
      },
      model: {
        id: 'modello',
        kicker: 'Il modello dichiarato',
        title: 'Come funziona Green Paradise: cosa include, cosa non include.',
        intro: 'Le voci qui sotto riflettono il modello dichiarato dal progetto. Non sono una promessa di rendimento: ogni voce va verificata nel contratto e con i tuoi consulenti.',
        items: [
          { icon: Percent, t: 'Revenue share 60/40 (RMA)', d: 'Ripartizione 60/40 dei ricavi lordi delle prenotazioni tra proprietario e gestione del resort, secondo il contratto di gestione (RMA).', flag: 'Da verificare nel contratto' },
          { icon: BadgeEuro, t: 'Nessun canone mensile', d: 'Secondo il modello dichiarato non è previsto un canone fisso mensile a carico del proprietario: la gestione si remunera sulla propria quota dei ricavi.', flag: 'Modello dichiarato' },
          { icon: Scale, t: 'Leasehold 90 anni', d: 'Diritto di godimento di lungo periodo (leasehold) della durata dichiarata di 90 anni, soggetto a verifica legale della documentazione.', flag: 'Verifica legale' },
          { icon: CalendarDays, t: 'Uso proprietario fino a 14 giorni/anno', d: 'Il proprietario può soggiornare nella propria villa fino a 14 giorni l’anno, secondo disponibilità e calendario del resort.', flag: 'Secondo disponibilità' },
          { icon: Utensils, t: 'Pasti ed escursioni inclusi · voli esclusi', d: 'Per i soggiorni del proprietario sono inclusi pasti ed escursioni; i voli restano esclusi.', flag: 'Soggiorni proprietario' },
          { icon: Users, t: 'Ospiti personali', d: 'L’accesso di ospiti personali (familiari, amici) segue le condizioni contrattuali: numero, periodi e costi vanno letti nel contratto.', flag: 'Da contratto' },
        ],
        disclaimer: 'Condizioni e disponibilità soggette a verifica contrattuale e legale. Nessuna promessa di rendimento: i ricavi dipendono da occupazione, tariffe e costi reali del resort.',
      },
      faq: {
        kicker: 'Domande frequenti',
        title: 'Costi, rischi, disponibilità, uscita e fiscalità: senza promesse.',
        items: [
          { q: 'Quali sono i costi, oltre al prezzo della villa?', a: 'Il prezzo dichiarato parte da €116.000 con piano pagamenti (caparra, anticipo, 5 rate, saldo). Costi accessori, imposte locali e spese contrattuali dipendono dal Paese e dalla tua situazione: vengono riepilogati nel dossier e verificati prima di qualsiasi impegno. Richiedere dossier e valutazione iniziale non costa nulla.' },
          { q: 'Quali sono i rischi principali?', a: 'Come in ogni operazione immobiliare all’estero: rischio Paese e normativo, rischio di esecuzione (tempi di completamento), rischio di mercato (occupazione e tariffe del resort) e rischio di cambio. Il percorso in due step serve proprio a leggerli con dati reali prima di decidere.' },
          { q: 'Com’è la disponibilità delle ville?', a: 'Il resort prevede 32 ville in due tipologie (Villa Green e Villa Paradise). La disponibilità aggiornata per tipologia viene confermata dal team al momento della richiesta: non pubblichiamo numeri non verificati.' },
          { q: 'Come funziona l’uscita? Posso rivendere?', a: 'Il diritto è un leasehold di lungo periodo (dichiarato 90 anni) e la rivendita segue le condizioni contrattuali e la normativa indonesiana. Modalità, eventuali vincoli e costi di uscita vanno verificati nel contratto con un legale: ne parliamo nella valutazione iniziale.' },
          { q: 'E la fiscalità?', a: 'La tassazione dipende dalla tua residenza fiscale e dalle norme indonesiane e italiane applicabili (redditi esteri, eventuali imposte locali). Non forniamo consulenza fiscale: ti indichiamo i punti da verificare con il tuo commercialista.' },
          { q: 'Quando apre il resort?', a: 'L’apertura dichiarata è prevista per ottobre 2027. Le date di cantiere e consegna vengono aggiornate dal team e condivise nel dossier: sono soggette a verifica.' },
        ],
      },
      ceoCall: {
        kicker: 'Percorso separato · alto valore',
        title: 'Vuoi valutare Lombok dentro una strategia patrimoniale più ampia?',
        text: 'La consulenza strategica col CEO di HCF Group non sostituisce dossier e valutazione iniziale: è un confronto per chi ha già chiaro l’obiettivo e vuole capire struttura, flussi e protezione.',
        cta: 'Prenota la consulenza col CEO',
      },
      success: {
        title: 'Richiesta ricevuta',
        subtitle: 'Step 1 avviato. Ecco cosa succede ora.',
        steps: [
          'La brochure ufficiale Green Paradise è in arrivo nella tua email.',
          'Un Senior Associate ti contatta per fissare la valutazione iniziale in Zoom.',
          'Solo dopo, se ha senso per te, organizziamo la verifica in loco a Bumbang Bay.',
        ],
        bookingCta: 'Vuoi anticipare? Prenota la consulenza col CEO',
        bookingNote: 'Facoltativa · percorso separato dalla valutazione iniziale.',
      },
    },
  },
  'es-ba': {
    code: 'ES-BA',
    lang: 'es',
    homeHref: '/f/es',
    funnelApi: '/public/funnel/es',
    propertySlug: 'villa-flora',
    name: 'Villa Flora',
    place: 'Bali · Indonesia',
    docTitle: 'Villa Flora Bali — HCF Property',
    docDescription: 'Villa Flora Bali: villa íntima con piscina privada, interiores luminosos y diseño boho-minimal. Solicita disponibilidad con HCF Property.',
    slides: [],
    slidesComponent: VillaFloraSlidesES,
    cta: 'Solicita disponibilidad',
    formKicker: 'Solicita disponibilidad · Villa Flora Bali',
    leadMessage: 'Solicitud de disponibilidad — Villa Flora (Bali, Indonesia) · funnel ES',
  },
  'es-lo': {
    code: 'ES-LO',
    lang: 'es',
    homeHref: '/f/es',
    funnelApi: '/public/funnel/es',
    propertySlug: 'es-lombok-green-paradise',
    name: 'Green Paradise Lombok',
    place: 'Bumbang Bay · Lombok · Indonesia',
    docTitle: 'Green Paradise Lombok — HCF Property',
    docDescription: 'Green Paradise Lombok: villas de lujo en Bumbang Bay. Primero se verifica, luego se analiza, solo después se elige la villa. Reserva la verificación in situ con HCF Property.',
    slides: [],
    slidesComponent: GreenParadiseSlidesES,
    cta: 'Reserva la verificación in situ',
    formKicker: 'Verificación in situ · Green Paradise Lombok',
    leadMessage: 'Solicitud verificación / análisis estratégico — Green Paradise Lombok (Indonesia) · funnel ES',
  },
  // ★ Fase 85 — versioni EN (EN-BA / EN-LO): lead → Richard (tenant EN)
  'en-ba': {
    code: 'EN-BA',
    lang: 'en',
    homeHref: '/f/en',
    funnelApi: '/public/funnel/en',
    propertySlug: 'villa-flora',
    name: 'Villa Flora',
    place: 'Bali · Indonesia',
    docTitle: 'Villa Flora Bali — HCF Property',
    docDescription: 'Villa Flora Bali: an intimate villa with private pool, bright interiors and boho-minimal design. Request availability with HCF Property.',
    slides: [],
    slidesComponent: VillaFloraSlidesEN,
    cta: 'Request availability',
    formKicker: 'Request availability · Villa Flora Bali',
    leadMessage: 'Availability request — Villa Flora (Bali, Indonesia) · EN funnel',
  },
  'en-lo': {
    code: 'EN-LO',
    lang: 'en',
    homeHref: '/f/en',
    funnelApi: '/public/funnel/en',
    propertySlug: 'green-paradise-lombok',
    name: 'Green Paradise Lombok',
    place: 'Bumbang Bay · Lombok · Indonesia',
    docTitle: 'Green Paradise Lombok — HCF Property',
    docDescription: 'Green Paradise Lombok: luxury villas in Bumbang Bay. First you verify, then you analyse, only then do you choose the villa. Book the on-site verification with HCF Property.',
    slides: [],
    slidesComponent: GreenParadiseSlidesEN,
    cta: 'Book the on-site verification',
    formKicker: 'On-site verification · Green Paradise Lombok',
    leadMessage: 'Verification / strategic analysis request — Green Paradise Lombok (Indonesia) · EN funnel',
  },
};

function LuxField({ icon: Icon, testId, ...props }) {
  return (
    <div className="relative">
      <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: GOLD }} strokeWidth={1.6} />
      <input
        {...props}
        data-testid={testId}
        className="aml-lux-field w-full h-12 rounded-xl pl-11 pr-4 text-sm font-sans"
      />
    </div>
  );
}

export default function AssetMiniLanding({ slug }) {
  const cfg = ASSET_LANDINGS[slug];
  const t = UI_TEXTS[cfg?.lang || 'it'];
  const v2 = cfg?.v2 || null; // ★ Fase 92/93 — layout evoluto (solo IT-BA / IT-LO)
  const primaryCta = v2?.ctaPrimary || cfg?.cta;
  const [calendly, setCalendly] = useState(CALENDLY_FALLBACK);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [prequal, setPrequal] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [leadId, setLeadId] = useState(null);

  useEffect(() => {
    if (i18n.language !== cfg.lang) i18n.changeLanguage(cfg.lang);
    // ★ Fase 89 — meta social: thumbnail = card asset (Bali / Lombok), og:url = link specifico
    setPageMeta({
      title: cfg.docTitle,
      description: cfg.docDescription,
      image: ogImageFor(slug),
      path: `/f/${slug}`,
      locale: cfg.lang,
    });
    captureAttribution(); // ★ Fase 91 — first/last touch, utm, click_id, landing_path
    window.scrollTo(0, 0);
    api.get(cfg.funnelApi)
      .then((r) => { if (r.data?.booking_url) setCalendly(r.data.booking_url); })
      .catch(() => {});
  }, [cfg.docTitle, cfg.docDescription, cfg.funnelApi, cfg.lang, slug]);

  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const scrollToForm = (label) => {
    trackFunnelEvent('cta_click', { funnel: slug, label: typeof label === 'string' ? label : primaryCta, target: 'form' });
    document.getElementById('aml-lead')?.scrollIntoView({ behavior: 'smooth' });
  };
  const onBookingClick = (position) => () => trackFunnelEvent('booking_redirect', { funnel: slug, lead_id: leadId, target: 'calendly', position });

  const submit = async (e) => {
    e?.preventDefault();
    if (name.trim().length < 2 || !emailRe.test(email.trim()) || sending) return;
    setSending(true);
    try {
      const pq = cleanPrequal(prequal);
      const r = await api.post('/public/funnel-lead', {
        funnel: slug, // ★ Fase 57 — lead taggato col SUO funnel (IT-BA / IT-LO)
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        property_slug: cfg.propertySlug,
        message: cfg.leadMessage,
        // ★ Fase 91 — attribution + prequalifica progressiva + intent (Doc 02)
        attribution: getAttribution(),
        prequal: pq,
        intent: v2?.intent || 'availability',
      });
      if (r.data?.lead_id) setLeadId(r.data.lead_id);
      setSent(true);
      toast.success(t.toastOk, { description: (cfg.brochureEmail && t.toastOkDescBrochure) || t.toastOkDesc });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.toastErr);
    } finally {
      setSending(false);
    }
  };

  if (!cfg) return null;

  const formValid = name.trim().length >= 2 && emailRe.test(email.trim());

  return (
    <div className={`relative min-h-screen font-serif ${v2 ? 'pb-24 md:pb-0' : ''}`} style={{ background: INK, color: IVORY }} data-testid={`asset-landing-${slug}`} data-layout={v2 ? 'v2' : 'v1'}>
      <style>{LUX_CSS}</style>
      {/* alone dorato soft dietro la colonna grafiche */}
      <div
        className="absolute inset-0 pointer-events-none" aria-hidden="true"
        style={{ background: `radial-gradient(900px 540px at 50% 0%, ${GOLD}14, transparent 70%)` }}
      />

      {/* ── Header sticky ── */}
      <header className="sticky top-0 z-40" style={{ background: `${INK}E8`, backdropFilter: 'blur(14px)' }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to={cfg.homeHref} data-testid="aml-logo-home">
            <img src="/funnels/hcf-logo.png" alt="HCF Property" className="h-9 w-auto" />
          </Link>
          {v2 ? (
            /* ★ Fase 92 — CTA header = CTA primaria (form), non la call high-value: niente cannibalizzazione */
            <button
              type="button" onClick={() => scrollToForm(v2.ctaShort)}
              className="aml-gold-btn rounded-full h-10 px-5 text-[11px] font-sans font-bold tracking-[0.2em] uppercase inline-flex items-center justify-center gap-2"
              data-testid="aml-header-cta"
            >
              <FileCheck2 className="w-4 h-4" /> {v2.ctaShort}
            </button>
          ) : (
            <a
              href={calendly} target="_blank" rel="noopener noreferrer"
              className="aml-gold-btn rounded-full h-10 px-5 text-[11px] font-sans font-bold tracking-[0.2em] uppercase inline-flex items-center justify-center gap-2"
              data-testid="aml-header-booking"
            >
              <CalendarClock className="w-4 h-4" /> {t.headerCta}
            </a>
          )}
        </div>
      </header>

      <main className="relative">
        {/* ★ Fase 92/93 — sopra la piega: proposta concreta, localizzazione, stato, micro-proof */}
        {v2 && <IntroHero intro={v2.intro} onPrimary={() => scrollToForm(v2.intro.cta)} />}

        {/* ── ★ Fase 57 — Grafiche a SCHERMO INTERO (edge-to-edge) ──
            Le slide riempiono tutta la larghezza del viewport, senza
            cornice né margini: 1→N compongono la landing continua. */}
        <div className="w-full" data-testid="aml-slides">
          {cfg.slidesComponent ? (
            /* ★ Fase 82/85 — ES/EN: slide HTML tradotte (stesse foto) */
            <cfg.slidesComponent onCta={scrollToForm} />
          ) : cfg.slides.map((src, i) => (
            <img
              key={src}
              src={src}
              alt={t.sectionAlt(cfg.name, i + 1, cfg.slides.length)}
              className="block w-full h-auto cursor-pointer"
              loading={i === 0 && !v2 ? 'eager' : 'lazy'}
              decoding="async"
              onClick={() => scrollToForm('slide')}
              data-testid={`aml-slide-${i + 1}`}
            />
          ))}
        </div>

        {/* ★ Fase 92 — micro-proof Bali · ★ Fase 93 — percorso 2 step + modello dichiarato Lombok */}
        {v2?.proof && <ProofStrip proof={v2.proof} />}
        {v2?.path && <StepPath path={v2.path} onPrimary={() => scrollToForm(v2.path.steps[0]?.cta)} />}
        {v2?.model && <ModelTerms model={v2.model} />}

        {/* CTA + form: colonna centrata leggibile sotto le grafiche */}
        <div className="max-w-2xl mx-auto px-5 md:px-8">
          <div className="mt-8">
            <button
              type="button" onClick={() => scrollToForm(primaryCta)}
              className="aml-gold-btn w-full rounded-full min-h-12 py-2.5 px-4 text-[11px] sm:text-[12px] font-sans font-bold tracking-[0.12em] sm:tracking-[0.2em] uppercase text-center leading-snug inline-flex items-center justify-center gap-2"
              data-testid="aml-cta"
            >
              {primaryCta} <ArrowRight className="w-4 h-4" />
            </button>
            {v2 && <NoObligationNote theme="dark" className="mt-3" testId="aml-cta-no-obligation" compact />}
          </div>

          {/* ── Form lead ── */}
          <section id="aml-lead" className="mt-14 pb-2 scroll-mt-20" data-testid="aml-lead-section">
          {sent && v2 ? (
            <NextStepsSuccess success={v2.success} calendly={calendly} onBooking={onBookingClick('success')} />
          ) : sent ? (
            <div
              className="rounded-2xl border p-8 text-center"
              style={{ borderColor: `${GOLD}66`, background: `${EMERALD}CC`, backdropFilter: 'blur(14px)' }}
              data-testid="aml-form-success"
            >
              <CheckCircle2 className="w-12 h-12 mx-auto" style={{ color: GOLD }} strokeWidth={1.4} />
              <div className="mt-4 text-2xl" style={{ color: IVORY }}>{(cfg.brochureEmail && t.successTitleBrochure) || t.successTitle}</div>
              <p className="mt-2 text-sm font-sans leading-relaxed" style={{ color: SAND }}>
                {cfg.brochureEmail && t.successDescBrochure ? t.successDescBrochure(cfg.name) : t.successDesc(cfg.name)}
              </p>
              <a
                href={calendly} target="_blank" rel="noopener noreferrer" onClick={onBookingClick('success')}
                className="aml-gold-btn mt-6 w-full rounded-full min-h-12 py-2.5 px-4 text-[11px] sm:text-[12px] font-sans font-bold tracking-[0.12em] sm:tracking-[0.2em] uppercase text-center leading-snug inline-flex items-center justify-center gap-2"
                data-testid="aml-success-booking"
              >
                <CalendarClock className="w-4 h-4" /> {t.bookRichard}
              </a>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="rounded-2xl border p-6 md:p-7 space-y-4"
              style={{ borderColor: `${GOLD}55`, background: `${INK}B8`, backdropFilter: 'blur(16px)', boxShadow: '0 24px 70px rgba(0,0,0,0.45)' }}
              data-testid="aml-form"
              noValidate
            >
              <div className="flex items-center gap-2 text-[11px] font-sans font-bold tracking-[0.25em] uppercase" style={{ color: GOLD }}>
                <FileCheck2 className="w-4 h-4" /> {cfg.formKicker}
              </div>
              <LuxField icon={User} placeholder={t.phName} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" testId="aml-form-name" aria-label={t.phName} required />
              <LuxField icon={Mail} type="email" placeholder={t.phEmail} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" testId="aml-form-email" aria-label={t.phEmail} required aria-invalid={email.length > 0 && !emailRe.test(email.trim())} />
              <LuxField icon={Phone} type="tel" placeholder={t.phPhone} value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" testId="aml-form-phone" aria-label={t.phPhone} />
              {email.length > 0 && !emailRe.test(email.trim()) && (
                <p className="text-[12px] font-sans -mt-2" style={{ color: '#E8B4A6' }} role="alert" data-testid="aml-form-email-error">{t.emailInvalid}</p>
              )}
              {/* ★ Fase 91 — step progressivo opzionale (obiettivo / budget / orizzonte) */}
              {v2 && <PrequalFields value={prequal} onChange={setPrequal} lang={cfg.lang} theme="dark" testIdPrefix="aml-prequal" />}
              <button
                type="submit"
                disabled={!formValid || sending}
                className="aml-gold-btn w-full rounded-full min-h-12 py-2.5 px-4 text-[11px] sm:text-[13px] font-sans font-bold tracking-[0.12em] sm:tracking-[0.2em] uppercase text-center leading-snug inline-flex items-center justify-center gap-2"
                data-testid="aml-form-submit"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {primaryCta} <ArrowRight className="w-4 h-4" />
              </button>
              {!v2 && (
                <a href={calendly} target="_blank" rel="noopener noreferrer" data-testid="aml-form-booking" onClick={onBookingClick('form')}>
                  <span className="mt-1 w-full rounded-full min-h-12 py-2.5 px-4 text-[11px] sm:text-[12px] font-sans font-bold tracking-[0.12em] sm:tracking-[0.2em] uppercase text-center leading-snug inline-flex items-center justify-center gap-2 border transition-colors duration-150 hover:bg-white/5" style={{ borderColor: GOLD, color: IVORY }}>
                    {t.bookRichard} <ArrowRight className="w-4 h-4" />
                  </span>
                </a>
              )}
              {v2 ? (
                <NoObligationNote theme="dark" className="pt-1" testId="aml-form-no-obligation" />
              ) : (
                <p className="pt-1 flex items-center justify-center gap-2 text-sm" style={{ color: IVORY }}>
                  <ShieldCheck className="w-5 h-5" style={{ color: GOLD }} /> {t.noObligation}
                </p>
              )}
              <p className="text-[11px] font-sans text-center leading-relaxed" style={{ color: SAND }}>
                {t.legal} <Link to="/privacy" className="underline underline-offset-2" style={{ color: IVORY }} data-testid="aml-form-privacy">{t.privacy}</Link>.
              </p>
            </form>
          )}
          <p className="mt-6 text-center text-sm font-sans" style={{ color: SAND }}>
            {t.backPrompt}{' '}
            <Link to={cfg.homeHref} className="underline underline-offset-4 hover:opacity-80" style={{ color: GOLD }} data-testid="aml-back-funnel">
              {t.backLink}
            </Link>
          </p>
          </section>
        </div>

        {/* ★ Fase 93 — FAQ (Lombok) · ★ Fase 92/93 — call high-value separata */}
        {v2?.faq && <FaqAccordion items={v2.faq.items} kicker={v2.faq.kicker} title={v2.faq.title} funnel={slug} theme="dark" testIdPrefix="aml-faq" className="mt-14" />}
        {v2?.ceoCall && <CeoCallCard card={v2.ceoCall} href={calendly} onClick={onBookingClick('ceo_card')} />}

        {/* ── ★ 2026-10-19 — Testimonianze video (fonte unica lib/testimonials.js) ── */}
        <TestimonialsSection lang={cfg.lang || 'it'} theme="dark" testIdPrefix="aml" id="testimonianze" className="mt-14" />
      </main>

      {/* ★ Fase 92 — CTA sticky mobile coerente (sparisce dopo l'invio) */}
      {v2 && <StickyCta label={v2.ctaShort} onClick={() => scrollToForm('sticky')} visible={!sent} />}

      {/* ── Footer ── */}
      <footer className="relative border-t mt-10" style={{ background: `${INK}D9`, backdropFilter: 'blur(10px)', borderColor: `${GOLD}26` }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div>
            <img src="/funnels/hcf-logo.png" alt="HCF Property" className="h-8" />
            <p className="text-[11px] font-sans mt-2" style={{ color: SAND }}>© {new Date().getFullYear()} HCF Property</p>
          </div>
          <div className="flex gap-5 text-xs font-sans items-center" style={{ color: SAND }}>
            <Link to="/privacy" className="underline hover:opacity-80" data-testid="aml-footer-privacy">{t.footerPrivacy}</Link>
            <Link to="/terms" className="underline hover:opacity-80" data-testid="aml-footer-terms">{t.footerTerms}</Link>
            <Link to="/cookie-policy" className="underline hover:opacity-80" data-testid="aml-footer-cookies">{t.footerCookies}</Link>
            <span className="tracking-[0.25em]" data-testid="aml-footer-code">{cfg.code}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
